<template>
  <div class="tools-page white-theme">
    <div class="container">
      <div class="page-header text-center">
        <span class="section-tag">{{ $t('tools.page.tag') }}</span>
        <h1 class="section-title">{{ $t('tools.page.title') }}</h1>
        <p class="section-subtitle">{{ $t('tools.page.subtitle') }}</p>
      </div>
      <div class="category-tabs">
        <button v-for="cat in categories" :key="cat.id" :class="['category-tab', { active: currentCategory === cat.id }]" @click="switchCategory(cat.id)">
          <span class="cat-icon">{{ cat.icon }}</span>
          <span>{{ $t(cat.nameKey) }}</span>
        </button>
      </div>
      <div class="tools-grid">
        <router-link v-for="t in filteredTools" :key="t.id" :to="getToolPath(t)" class="card tool-card">
          <div class="tool-card-cover">
            <template v-if="getDemoImages(t.id)">
              <ToolDemoCover :images="getDemoImages(t.id)" :alt="getToolText(t.id, 'name')" />
            </template>
            <DocFormatCover
              v-else-if="isDocTool(t.id)"
              :tool-id="t.id"
              mode="cover"
            />
            <div v-else class="tool-card-fallback">
              <span class="tool-card-fallback-icon">{{ t.icon }}</span>
            </div>
            <span v-if="t.badge" :class="['tool-card-badge', t.badge === 'Hot' ? 'badge-hot' : 'badge-new']">{{ t.badge }}</span>
          </div>
          <div class="tool-card-body">
            <h3>{{ getToolText(t.id, 'name') }}</h3>
            <p>{{ getToolText(t.id, 'desc') }}</p>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
var getToolDemoImages = require('@/config/toolDemoImages').getToolDemoImages
var isDocumentTool = require('@/config/docConvertVisuals').isDocumentTool
var DocFormatCover = require('@/components/DocFormatCover.vue').default
var ToolDemoCover = require('@/components/ToolDemoCover.vue').default

export default {
  name: 'Tools',
  components: { DocFormatCover: DocFormatCover, ToolDemoCover: ToolDemoCover },
  data: function () {
    return {
      currentCategory: 'image'
    }
  },
  computed: {
    categories: function () {
      return this.$store.state.toolCategories || []
    },
    filteredTools: function () {
      var cat = this.currentCategory
      return this.$store.state.tools.filter(function (t) {
        return t.id !== 'music-generator' && t.category === cat
      })
    }
  },
  watch: {
    '$route.query.category': {
      immediate: true,
      handler: function (val) {
        if (val === 'document' || val === 'image' || val === 'video') {
          this.currentCategory = val
        } else {
          this.currentCategory = 'image'
        }
      }
    }
  },
  methods: {
    switchCategory: function (catId) {
      this.currentCategory = catId
      var lang = this.$route.params.lang
      var path = (lang ? '/' + lang : '') + '/tools?category=' + catId
      this.$router.replace(path).catch(function () {})
    },
    toolPath: function (toolId) {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/tools/' + toolId : '/tools/' + toolId
    },
    getToolPath: function (t) {
      if (t.category === 'document') {
        var lang = this.$route.params.lang
        return lang ? '/' + lang + '/tools/doc-convert/' + t.id : '/tools/doc-convert/' + t.id
      }
      return this.toolPath(t.id)
    },
    getToolText: function (id, field) {
      var tools = this.$t('tools')
      if (tools && typeof tools === 'object' && tools[id] && tools[id][field]) {
        return tools[id][field]
      }
      return this.$t('tools.' + id + '.' + field)
    },
    getDemoImages: function (toolId) {
      return getToolDemoImages(toolId)
    },
    isDocTool: function (toolId) {
      return isDocumentTool(toolId)
    }
  }
}
</script>

<style scoped>
.tools-page { padding: 48px 0 80px; }
.page-header { margin-bottom: 32px; }
.category-tabs { display: flex; justify-content: center; gap: 12px; margin-bottom: 32px; }
.category-tab {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 24px; border-radius: 12px;
  font-size: 14px; font-weight: 500;
  color: var(--text-secondary); background: var(--bg-card);
  border: 1px solid var(--bg-card-border);
  cursor: pointer; transition: all 0.2s;
}
.category-tab:hover { border-color: var(--accent); color: var(--accent); }
.category-tab.active {
  background: rgba(5, 150, 105, 0.1);
  border-color: var(--accent);
  color: var(--accent);
  font-weight: 600;
}
.cat-icon { font-size: 18px; }
.tools-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
.tool-card {
  display: block;
  padding: 0;
  position: relative;
  height: auto;
  box-sizing: border-box;
  overflow: hidden;
  text-align: left;
}
.tool-card-cover {
  position: relative;
  display: flex;
  aspect-ratio: 16 / 9;
  background: var(--bg-secondary);
  overflow: hidden;
  transition: opacity 0.2s;
}
.tool-card-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background: linear-gradient(135deg, var(--bg-secondary), var(--bg-primary));
}
.tool-card-fallback-icon {
  font-size: 28px;
  line-height: 1;
}
.tool-card-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 2;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
}
.tool-card-body {
  padding: 8px 10px 10px;
}
.tool-card h3 {
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tool-card p {
  font-size: 11px;
  color: var(--text-muted);
  line-height: 1.35;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tool-card:hover .tool-card-cover {
  opacity: 0.92;
}
@media (max-width: 1024px) {
  .tools-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .tools-grid { grid-template-columns: 1fr; gap: 12px; }
}
</style>
